import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useHistory } from "react-router-dom";
import { CustomDialog } from "../CustomDialog";
import { useVerticalNav } from "../../../Contexts/VerticalNavContext";
import { useSelectedTheme, useTranslate } from "../../../Hooks";
import { Box, Divider, Typography } from "@material-ui/core";
import { CustomAutocomplete } from "../CustomAutocomplete";
import { CustomInput } from "../CustomInput";
import { CustomCopyToClipboard } from "../CustomCopyToClipboard";
import { CustomButton } from "../CustomButton";
import {
  CityTypeIdEnum,
  ContactClassIdEnum,
  LanguageEnum,
  NationalityEnum,
} from "../../../Enums";
import { useNewLayoutShared } from "../../../Contexts/NewLayoutSharedContext";
import { CreateContactCorporate, lookupItemsGetId } from "../../../Services";
import { DropzoneComponent } from "../DropzoneComponent";
import { formatBytes } from "react-dropzone-uploader";
import { GetFileTypeIcon } from "../../../Views/Home/NewContactsView/ContactOverview/UI";
import { CustomCard } from "../CustomCard";
import { OnAddnewContactRule } from "../../../Rule/ContactRule";

// Icons
import { DeleteIcon } from "../../../assets/icons";

// Styles
import useStyles from "./styles";

const initialState = {
  classifications: [],
  country: null,
  city: null,
  landline: [],
  language: null,
  logoGUID: null,
  companyName: "",
  website: "",
};

const initialSelected = {
  classifications: null,
  country: null,
  city: null,
  language: null,
  logoGUID: null,
  website: "",
};

const initialStateData = {
  classificationsData: [],
  countryData: [],
  cityData: [],
  languageData: [],
};

const reducer = (state, { field, value }) => {
  if (field === "reset") {
    return initialState;
  }
  return { ...state, [field]: value };
};

const AddNewCorporateContactDialog = ({ onClose, onOpen }) => {
  const styles = useStyles();
  
  const { translate } = useTranslate("HeaderView");
  
  const history = useHistory();

  const {
    theme: { palette },
  } = useSelectedTheme();
  
  const { addNewContact, setAlertBoxContent, setAddNewContact } = useVerticalNav();
  const [state, setState] = useReducer(reducer, initialState);
  const [selected, setSelected] = useReducer(reducer, initialSelected);
  const [data, setData] = useReducer(reducer, initialStateData);
  const [isSubmit, setIsSubmit] = useState(false);
  const [filesAccepted, setFilesAccepted] = useState([]);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const { showDialog } = useNewLayoutShared();
  const { translate: sharedTranslate } = useTranslate('SharedV2');

  const handleFilesAccepted = (files) => {
    setFilesAccepted(files);
  };

  const getAllLanguages = useCallback(async () => {
    try {
      const res = await lookupItemsGetId({
        lookupTypeId: LanguageEnum.lookupTypeId,
      });

      if (!(res && res.status && res.status !== 200))
        setData({ field: "languageData", value: res });
      else setData({ field: "languageData", value: [] });
    } catch (error) {}
  }, []);

  const getAllContactClass = useCallback(async () => {
    const res = await lookupItemsGetId({
      lookupTypeId: ContactClassIdEnum.lookupTypeId,
    });

    if (!(res && res.status && res.status !== 200))
      setData({ field: "classificationsData", value: res });
    else setData({ field: "classificationsData", value: [] });
  }, []);

  const GetCountries = useCallback(async () => {
    const res = await lookupItemsGetId({
      lookupTypeId: NationalityEnum.lookupTypeId,
    });

    if (!(res && res.status && res.status !== 200)) {
      setData({ field: "countryData", value: res });
    } else setData({ field: "countryData", value: [] });
  }, []);

  const GetCities = useCallback(async (countryId) => {
    const res = await lookupItemsGetId({
      lookupTypeId: CityTypeIdEnum.lookupTypeId,
      lookupParentId: countryId,
    });

    if (!(res && res.status && res.status !== 200)) {
      setData({ field: "cityData", value: res });
    } else setData({ field: "cityData", value: [] });
  }, []);

  const addCorporateContact = useCallback(
    async (isSavedAndComplete = false) => {
      let res = null;
      try {
        setIsSubmit(true);
        const body = {
          classifications: state?.classifications?.[0]
            ? state?.classifications
            : [],
          ...(state?.country && { countryId: state?.country }),
          ...(state?.city && { cityId: state.city }),
          ...(state?.language && { languageId: state?.language }),
          landlineNumber: state?.landline,
          logoGUID: state?.logoGUID,
          companyName: state?.companyName,
          website: state?.website,
        };
        res = await CreateContactCorporate(body);
        const res = await CreateContactCorporate(body);
        if (!(res && res.status && res.status !== 200)) {
          setAlertBoxContent({
            display: true,
            variant: "success",
            title: "Contact added successfully!",
            onClose: () => {
              setAlertBoxContent(null);
            },
          });
          onClose();

          if (res?.contactId) {
            showDialog(sharedTranslate("contact-successfully"), {
              name: res?.companyName,
              id: res?.contactId,
            });
          }
        } else {
          setAlertBoxContent({
            display: true,
            variant: "error",
            title: "Failed to add the contact.",
            onClose: () => {
              setAlertBoxContent(null);
            },
          });
        }
        if (isSavedAndComplete) {
          // handle the form builder
          history.push(
            `/home/Contacts-CRM/contact-profile-edit?formType=2&id=${res?.contactId}`
          );
        }
      } catch (error) {
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: "Some Thing wrong.",
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      } finally {
        setIsSubmit(false);
        setData(addNewContact?.contact);
        setAddNewContact((prev) => ({
          ...prev,
          contact: { ...prev?.contact, selected },
          addContactModalOpened: false,
        }));
        if (addNewContact?.isFormBuilderContact) {
          OnAddnewContactRule(
            addNewContact?.item,
            addNewContact?.itemList,
            addNewContact?.setData,
            addNewContact?.contact
          );

          if (addNewContact?.setInputValue) {
            addNewContact.setInputValue(addNewContact?.contact?.companyName);
          }
          if (addNewContact?.setNewValue) {
            addNewContact.setNewValue({
              name: `${addNewContact?.contact?.companyName}`,
              id: res?.contactId,
              type: res?.companyName ? 2 : 1,
              email_address: addNewContact?.contact?.email,
              mobile: addNewContact?.contact?.landline,
            });
          }
          if (addNewContact?.setData) {
            addNewContact.setData(addNewContact?.item.field?.id, {
              name: `${addNewContact?.contact?.companyName}`,
              id: res?.contactId,
              type: res?.companyName ? 2 : 1,
              email_address: addNewContact?.contact?.email,
              mobile: addNewContact?.contact?.landline,
            });
          }
        }
      }
      onClose();
    },
    [state]
  );

  const handleDeleteUploadedFile = (index) => {
    setUploadedFiles((prevUploadedFiles) =>
      prevUploadedFiles.filter((_, i) => i !== index)
    );
    setState({ field: "logoGUID", value: null });
  };

  useEffect(() => {
    getAllLanguages();
    getAllContactClass();
    GetCountries();
  }, []);

  useEffect(() => {
    if (uploadedFileId) {
      setState({ field: "logoGUID", value: uploadedFileId });
      const file = filesAccepted[0];
      const newFile = {
        ...file,
        fileId: uploadedFileId,
        fileSize: file.size,
        fileName: file.path,
        fileType: file.type,
      };
      setUploadedFiles([newFile]);
    } else {
      setState({ field: "logoGUID", value: null });
    }
  }, [uploadedFileId]);

  useEffect(() => {
    setState({
      field: "companyName",
      value: addNewContact?.contact?.companyName,
    });
    setState({ field: "landline", value: [addNewContact?.contact?.landline] });
  }, [addNewContact]);

  return (
    <CustomDialog
      open={onOpen}
      onClose={onClose}
      title={translate("Add-new-corporate")}
      subtitle={translate(
        "Create-a-new-contact-entry-specifying-whether-its-an-individual-or-corporate-entity-to-categorize-and-manage-contacts-effectively"
      )}
      width={"1024px"}
      confirmText={translate("Add-contact")}
      isDisabled={!state?.language || isSubmit}
      contentDialogClass={styles.containerContentDialog}
      onConfirm={() => {
        addCorporateContact();
      }}
      content={
        <Box className={styles.wrapperAddCorrporateDialog}>
          <Box className={styles.containerLeftSide}>
            <Box className={styles.containerItemInfo}>
              <Typography className={styles.lableInfo}>
                {translate("Company-name")}
              </Typography>
              <Typography className={styles.valueInfo}>
                {addNewContact?.contact?.companyName}
              </Typography>
            </Box>

            <Box className={styles.containerItemInfo}>
              <Typography className={styles.lableInfo}>
                {translate("Landline")}
              </Typography>
              <Typography className={styles.valueInfo}>
                {addNewContact?.contact?.landline}
              </Typography>
            </Box>
          </Box>

          <Box className={styles.containerRightSide}>
            <Box className={styles.containerInputFiled}>
              <Typography className={styles.titleInput}>
                {translate("Languages")}*
              </Typography>
              <CustomAutocomplete
                placeholder={translate("Select")}
                options={data?.languageData}
                value={selected?.language || null}
                onChange={(e, value) => {
                  setSelected({ field: "language", value: value });
                  setState({ field: "language", value: value?.lookupItemId });
                }}
                getOptionLabel={(option) => option?.lookupItemName || ""}
                getOptionValue={(option) => option?.lookupItemName || ""}
                renderOption={(option) => option?.lookupItemName || ""}
              />
            </Box>
            <Divider />

            <Box className={styles.containerInputFiled}>
              <Typography className={styles.titleInput}>
                {translate("Contact-class")}
              </Typography>
              <CustomAutocomplete
                placeholder={translate("Select")}
                options={data?.classificationsData}
                value={selected?.classifications}
                onChange={(e, value) => {
                  setSelected({ field: "classifications", value: value });
                  setState({
                    field: "classifications",
                    value: [value?.lookupItemId],
                  });
                }}
                getOptionLabel={(option) => option?.lookupItemName || ""}
                getOptionValue={(option) => option?.lookupItemName || ""}
                renderOption={(option) => option?.lookupItemName || ""}
              />
            </Box>
            <Divider />

            <Box className={styles.containerInputFiled}>
              <Typography className={styles.titleInput}>
                {translate("Country")}
              </Typography>
              <CustomAutocomplete
                placeholder={translate("Select")}
                options={data?.countryData}
                value={selected?.country || null}
                onChange={(e, value) => {
                  setSelected({ field: "country", value: value });
                  setState({ field: "country", value: value?.lookupItemId });
                  setSelected({ field: "city", value: null });
                  setState({ field: "city", value: null });

                  if (value) GetCities(value?.lookupItemId);
                  else {
                    setData({ field: "city", value: [] });
                    setSelected({ field: "city", value: null });
                    setState({ field: "city", value: null });
                  }
                }}
                getOptionLabel={(option) => option?.lookupItemName || ""}
                getOptionValue={(option) => option?.lookupItemName || ""}
                renderOption={(option) => option?.lookupItemName || ""}
              />
            </Box>
            <Divider />

            <Box className={styles.containerInputFiled}>
              <Typography className={styles.titleInput}>
                {translate("City")}
              </Typography>
              <CustomAutocomplete
                placeholder={translate("Select")}
                options={data?.cityData}
                value={selected?.city || null}
                onChange={(e, value) => {
                  setSelected({ field: "city", value: value });
                  setState({ field: "city", value: value?.lookupItemId });
                }}
                getOptionLabel={(option) => option?.lookupItemName || ""}
                getOptionValue={(option) => option?.lookupItemName || ""}
                renderOption={(option) => option?.lookupItemName || ""}
              />
            </Box>
            <Divider />

            <Box className={styles.containerInputFiled}>
              <Typography className={styles.titleInput}>
                {translate("Website-URL")}
              </Typography>
              <Box className={styles.containerWebsiteFiled}>
                <CustomInput
                  hasSearchIcon={false}
                  style={{ maxWidth: "100%" }}
                  placeholder={translate("Enter")}
                  onChange={(e) => {
                    setSelected({ field: "website", value: e.target.value });
                    setState({ field: "website", value: e.target.value });
                  }}
                  value={selected.website}
                />
                <CustomCopyToClipboard
                  data={state?.website ? state?.website : " "} // copy
                  copyDoneShowTime={1000}
                  customText={translate("Url-copied")}
                />
              </Box>
            </Box>
            <Divider />

            <Box className={styles.containerInputFiled}>
              <Typography className={styles.titleInput}>
                {translate("Logo")}
              </Typography>
              <Box className={styles.uploadedSection}>
                <DropzoneComponent
                  accept={"image/*"}
                  title={translate("SVG-PNG-JPG-or-GIF-max-800x400px")}
                  onFilesAccepted={handleFilesAccepted}
                  setUploadedFileId={setUploadedFileId}
                />
                {uploadedFiles && uploadedFiles?.length > 0 && (
                  <Box className={styles.containerUpladedFilesCrads}>
                    {uploadedFiles
                      .filter((file) => file?.fileType)
                      .map((uploadFile, index) => (
                        <CustomCard
                          boxShadow="xs"
                          borderRadius="xl"
                          borderColor="secondary"
                          classes={styles.CardUploadedFilesInfo}
                          key={index}
                        >
                          {GetFileTypeIcon(uploadFile?.fileType)}
                          <Box className={styles.ContainerFileUploadedInfo}>
                            <Box className={styles.containerTitleAndDeleteIcon}>
                              <Typography className={styles.TitleFileName}>
                                {uploadFile?.fileName}
                              </Typography>
                              <DeleteIcon
                                width="20"
                                height="20"
                                className={styles.DeleteIcon}
                                style={{ cursor: "pointer" }}
                                fill={palette.button.secondary_fg}
                                onClick={() => handleDeleteUploadedFile(index)}
                              />
                            </Box>

                            <Typography className={styles.FileSizeTitle}>
                              {formatBytes(uploadFile?.fileSize)}
                            </Typography>
                          </Box>
                        </CustomCard>
                      ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      }
      moreAction={
        <CustomButton
          boxShadow="none"
          size="lg"
          variant="text"
          color="tertiaryGray"
          onClick={() => {
            addCorporateContact(true);
          }}
          disabled={!state?.language || isSubmit}
          style={{ marginInlineEnd: "auto" }}
        >
          {translate("Save-And-Complete-Details")}
        </CustomButton>
      }
    />
  );
};

export default AddNewCorporateContactDialog;
